import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { IconCheck, IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { ensureFontPreview, previewFamily } from '../../../../../features/print-template-editor/fonts';
import { BN_FONT_NAMES, NOTICE_FONT_FAMILIES } from './noticeGeneratorEngine';

/* ------------------------------------------------------------------ *
 *  Family picker — the print editor's, in the tools' clothes.
 *
 *  Only the families that carry Bengali are listed (see NOTICE_FONT_FAMILIES),
 *  so there is no windowing and no writing-system filter here: the list is a
 *  dozen rows, and every one of them can actually set a Bangla notice.
 *
 *  Each row's preview face is still fetched only when it scrolls into view —
 *  a name-sized subset per family, rather than a dozen full downloads to open
 *  a dropdown.
 * ------------------------------------------------------------------ */

const ROW_H = 34;

/**
 * One row: the family's name, set in its own face once that has arrived and in
 * the UI font until then — never blank, so the list stays readable.
 *
 * Bangla readers know these faces by their Bangla names, so the ones that have
 * one lead with it and carry the Latin name alongside.
 */
function FontRow({ family, selected, active, onPick, onHover }) {
    const [ready, setReady] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        let alive = true;
        const io = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            io.disconnect();
            ensureFontPreview(family).then((ok) => { if (alive && ok) setReady(true); });
        }, { rootMargin: '120px' });
        io.observe(node);
        return () => { alive = false; io.disconnect(); };
    }, [family]);

    const bn = BN_FONT_NAMES[family];
    return (
        <button
            ref={ref}
            type="button"
            onClick={() => onPick(family)}
            onMouseEnter={onHover}
            title={family}
            className={`flex h-[34px] w-full shrink-0 items-center gap-2 rounded-lg px-2 text-left transition-colors ${
                active ? 'bg-[#F7F5F3] dark:bg-zinc-800' : ''
            } ${selected ? 'text-amber-600 dark:text-amber-400' : 'text-[#3D3833] dark:text-zinc-200'}`}
        >
            <span className="flex w-3.5 shrink-0 items-center justify-center">
                {selected ? <IconCheck size={13} stroke={2.6} /> : null}
            </span>
            <span
                className="min-w-0 flex-1 truncate text-[13.5px] leading-none"
                style={{
                    fontFamily: ready
                        ? `"${previewFamily(family)}", "${family}", sans-serif`
                        : `"${family}", sans-serif`,
                }}
            >
                {bn || family}
            </span>
            {bn ? (
                <span className="shrink-0 text-[10.5px] text-[#A8A099] dark:text-zinc-500">
                    {family}
                </span>
            ) : null}
        </button>
    );
}

/**
 * @param {{ value: string, onChange: (family: string) => void, label?: string }} props
 *   `value` is a family NAME, the same key the catalogue and the canvas use.
 */
export default function NoticeFontPicker({ value, onChange, label }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [cursor, setCursor] = useState(0);
    const listRef = useRef(null);
    const searchRef = useRef(null);

    const items = useMemo(() => {
        const q = query.trim();
        if (!q) return NOTICE_FONT_FAMILIES;
        const lower = q.toLowerCase();
        // Bangla names are searchable too — someone looking for the face they
        // picked last time types what the row said, not its Latin name.
        return NOTICE_FONT_FAMILIES.filter((name) => (
            name.toLowerCase().includes(lower) || (BN_FONT_NAMES[name] || '').includes(q)
        ));
    }, [query]);

    // Re-anchor whenever the result set changes, so the highlight is never left
    // pointing past the end of a freshly-filtered list.
    useEffect(() => { setCursor(0); }, [query]);

    // Open on the current font rather than at the top, and focus the search box.
    useEffect(() => {
        if (!open) return;
        const i = items.indexOf(value);
        setCursor(i >= 0 ? i : 0);
        requestAnimationFrame(() => searchRef.current?.focus());
        // Only on open — re-running on every keystroke would fight the search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pick = (name) => {
        onChange(name);
        setOpen(false);
        setQuery('');
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = Math.min(items.length - 1, Math.max(0, cursor + (e.key === 'ArrowDown' ? 1 : -1)));
            setCursor(next);
            listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (items[cursor]) pick(items[cursor]);
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={label}
                    className={`flex h-[42px] w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-left text-[13.5px] text-[#3D3833] transition-colors dark:bg-zinc-900 dark:text-zinc-200 ${
                        open
                            ? 'border-amber-400 ring-1 ring-amber-400'
                            : 'border-[#ECE8E4] hover:border-amber-400/50 dark:border-zinc-700'
                    }`}
                >
                    <span
                        className="min-w-0 truncate"
                        style={{ fontFamily: `"${value}", "Hind Siliguri", sans-serif` }}
                    >
                        {BN_FONT_NAMES[value] || value}
                    </span>
                    <IconChevronDown size={15} className="shrink-0 text-[#A8A099] dark:text-zinc-500" />
                </button>
            </PopoverTrigger>

            <PopoverContent
                align="start"
                sideOffset={6}
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="w-[var(--radix-popover-trigger-width)] min-w-[260px] overflow-hidden rounded-2xl border-[#ECE8E4] p-0 dark:border-zinc-700 dark:bg-zinc-900"
            >
                <div className="border-b border-[#ECE8E4] px-3 pb-2.5 pt-3 dark:border-zinc-700">
                    <div className="relative">
                        <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A8A099] dark:text-zinc-500" />
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder={t('noticeGeneratorImage.fontSearch')}
                            className="h-9 w-full rounded-lg border border-[#ECE8E4] bg-white pl-8 pr-8 text-[13px] text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                        {query ? (
                            <button
                                type="button"
                                onClick={() => { setQuery(''); searchRef.current?.focus(); }}
                                aria-label={t('common.clear')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A8A099] hover:text-[#3D3833] dark:text-zinc-500 dark:hover:text-zinc-200"
                            >
                                <IconX size={14} />
                            </button>
                        ) : null}
                    </div>
                </div>

                <div
                    ref={listRef}
                    onKeyDown={onKeyDown}
                    tabIndex={-1}
                    className="overflow-y-auto px-1.5 py-1.5"
                    // Tall enough for the whole list, capped so a longer one scrolls.
                    style={{ maxHeight: ROW_H * 9 }}
                >
                    {items.length === 0 ? (
                        <div className="px-2 py-6 text-center text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('noticeGeneratorImage.fontNoMatch')}
                        </div>
                    ) : items.map((family, i) => (
                        <FontRow
                            key={family}
                            family={family}
                            selected={family === value}
                            active={i === cursor}
                            onHover={() => setCursor(i)}
                            onPick={pick}
                        />
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
